import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Dropdown, Form } from 'semantic-ui-react';
import { Popup } from '../../lib/custom-ui';

import { useForm } from '../../hooks';

import styles from './ListCloneStep.module.scss';

const ListCloneStep = React.memo(
  ({ projectsToLists, defaultPath, onClone, onBack, onClose, isSubmitting }) => {
    const [t] = useTranslation();

    const [path, handleFieldChange, setPath] = useForm(() => ({
      projectId: null,
      boardId: null,
      navigateAfterClone: false,
      ...defaultPath,
    }));

    const selectedProject = useMemo(
      () =>
        projectsToLists.find((project) => String(project.id) === String(path.projectId)) || null,
      [projectsToLists, path.projectId],
    );

    const handleProjectIdChange = useCallback(
      (_, data) => {
        setPath((prevPath) => ({
          ...prevPath,
          projectId: data.value,
          boardId: null,
        }));
      },
      [setPath],
    );

    const handleBoardIdChange = useCallback(
      (event, data) => {
        handleFieldChange(event, data);
      },
      [handleFieldChange],
    );

    const handleNavigateAfterCloneChange = useCallback(
      (_, { checked }) => {
        setPath((prevPath) => ({
          ...prevPath,
          navigateAfterClone: checked,
        }));
      },
      [setPath],
    );

    const selectedBoard = useMemo(() => {
      if (!selectedProject || !path.boardId) {
        return null;
      }

      return (
        selectedProject.boards.find((board) => String(board.id) === String(path.boardId)) || null
      );
    }, [selectedProject, path.boardId]);

    const handleSubmit = useCallback(() => {
      if (!path.boardId || !selectedBoard) {
        return;
      }

      onClone(path.boardId, path.navigateAfterClone);
    }, [onClone, path.boardId, path.navigateAfterClone, selectedBoard]);

    const isBoardReady = !!selectedBoard;

    return (
      <>
        <Popup.Header onBack={onBack}>
          {t('common.cloneList', {
            context: 'title',
            defaultValue: 'Клонирование списка',
          })}
        </Popup.Header>
        <Popup.Content>
          <Form onSubmit={handleSubmit}>
            <div className={styles.text}>{t('common.project')}</div>
            <Dropdown
              fluid
              selection
              name="projectId"
              options={projectsToLists.map((project) => ({
                text: project.name,
                value: project.id,
              }))}
              value={selectedProject && selectedProject.id}
              placeholder={
                projectsToLists.length === 0 ? t('common.noProjects') : t('common.selectProject')
              }
              disabled={projectsToLists.length === 0 || isSubmitting}
              className={styles.field}
              onChange={handleProjectIdChange}
            />
            {selectedProject && (
              <>
                <div className={styles.text}>{t('common.board')}</div>
                <Dropdown
                  fluid
                  selection
                  name="boardId"
                  options={selectedProject.boards.map((board) => ({
                    text: board.name,
                    value: board.id,
                  }))}
                  value={path.boardId || null}
                  placeholder={
                    selectedProject.boards.length === 0
                      ? t('common.noBoards')
                      : t('common.selectBoard')
                  }
                  disabled={selectedProject.boards.length === 0 || isSubmitting}
                  className={styles.field}
                  onChange={handleBoardIdChange}
                />
              </>
            )}
            <div className={styles.switchRow}>
              <Checkbox
                checked={path.navigateAfterClone}
                disabled={isSubmitting}
                onChange={handleNavigateAfterCloneChange}
              />
              <span className={styles.switchLabel}>
                {t('common.navigateAfterClone', {
                  defaultValue: 'Перейти после копирования',
                })}
              </span>
            </div>
            <Button
              positive
              content={t('action.clone', { defaultValue: 'Клонировать' })}
              disabled={!isBoardReady || isSubmitting}
              loading={isSubmitting}
            />
          </Form>
        </Popup.Content>
      </>
    );
  },
);

ListCloneStep.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  projectsToLists: PropTypes.array.isRequired,
  defaultPath: PropTypes.object.isRequired,
  /* eslint-enable react/forbid-prop-types */
  onClone: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};

ListCloneStep.defaultProps = {
  onBack: undefined,
  isSubmitting: false,
};

export default ListCloneStep;
